import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowLeftRight,
  ArrowUp,
  Building2,
  Check,
  ChevronRight,
  ChevronsUpDown,
  CircleAlert,
  Globe,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import { Stakeholder, Assignment } from '../../types/stakeholder';
import { stakeholderService } from '../../services/stakeholderService';
import { LockMarker } from '../../features/confidentiality';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { Pagination } from '../../components/common/Pagination';
import { SelectionActionBar } from '../../components/common/SelectionActionBar';
import { MergeResolutionSheet, MergeFieldGroup } from '../../components/common/MergeResolutionSheet';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/common';
import { canDeleteStakeholder, canUpdateStakeholder, canMergeStakeholders } from '../../utils/permissions';
import { useDebounce } from '../../hooks/useDebounce';
import { fuzzyFilter, highlightMatch } from '../../utils/fuzzySearch';
import { useTaxonomyColors } from '../../hooks/useTaxonomyColors';
import {
  Avatar,
  Button,
  Card,
  CategoryPill,
  Checkbox,
  EmptyState,
  IconButton,
  Menu,
  MenuItem,
  Page,
  PageHeader,
  Pill,
  SearchInput,
  SegmentedControl,
  Skeleton,
  statusTone,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../components/ui';

type SortField = 'firstName' | 'lastName' | 'category' | 'status' | 'createdAt';
type SortOrder = 'ASC' | 'DESC';
type StatusValue = 'all' | 'approved' | 'pending' | 'draft' | 'rejected';

const DEFAULT_LIMIT = 25;
/** Floats the pagination above the selection bar (its 48px pill and 24px margin) */
const SELECTION_BAR_CLEARANCE = 84;

const STATUS_ITEMS: Array<{ value: StatusValue; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'draft', label: 'Draft' },
  { value: 'rejected', label: 'Rejected' },
];

const STAKEHOLDER_MERGE_FIELDS: MergeFieldGroup[] = [
  {
    title: 'Personal Information',
    icon: User,
    fields: [
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'bio', label: 'Bio' },
    ],
  },
  {
    title: 'Contact Details',
    icon: Phone,
    fields: [
      { key: 'professionalEmail', label: 'Professional Email' },
      { key: 'personalEmail', label: 'Personal Email' },
      { key: 'professionalPhone', label: 'Professional Phone' },
      { key: 'personalPhone', label: 'Personal Phone' },
    ],
  },
  {
    title: 'Professional Info',
    icon: Building2,
    fields: [
      { key: 'location', label: 'Location' },
      { key: 'category', label: 'Category' },
    ],
  },
  {
    title: 'Social & Web',
    icon: Globe,
    fields: [
      { key: 'linkedinUrl', label: 'LinkedIn' },
      { key: 'twitterUrl', label: 'X (Twitter)' },
      { key: 'facebookUrl', label: 'Facebook' },
      { key: 'telegramUrl', label: 'Telegram' },
      { key: 'whatsapp', label: 'WhatsApp' },
    ],
  },
];

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <Pill tone={statusTone(status)}>{status.charAt(0).toUpperCase() + status.slice(1)}</Pill>
);

/** "media-journalist-influencer" → "Media journalist influencer" */
const categoryLabel = (category: string) => {
  const text = category.replace(/[-_]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const HighlightedText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const segments = highlightMatch(text, query);
  return (
    <>
      {segments.map((seg, i) =>
        seg.highlight ? (
          <mark key={i} className="rounded-[3px] bg-primary-50 text-primary-700">
            {seg.text}
          </mark>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
};

const SortTH: React.FC<{
  label: string;
  field: SortField;
  currentSort: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
}> = ({ label, field, currentSort, currentOrder, onSort, className = '' }) => {
  const active = currentSort === field;
  const Icon = !active ? ChevronsUpDown : currentOrder === 'ASC' ? ArrowUp : ArrowDown;
  return (
    <TH
      scope="col"
      aria-sort={active ? (currentOrder === 'ASC' ? 'ascending' : 'descending') : 'none'}
      className={className}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`group -ml-1 inline-flex h-7 items-center gap-1 rounded-full px-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
          active ? 'text-ink' : 'text-neutral-400 hover:text-ink'
        }`}
      >
        {label}
        <Icon
          className={`h-3 w-3 ${active ? 'text-ink' : 'text-neutral-300 group-hover:text-neutral-400'}`}
          strokeWidth={2}
        />
      </button>
    </TH>
  );
};

export const StakeholderListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { colorOf } = useTaxonomyColors();

  // URL-driven state
  const initialSearch = searchParams.get('q') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const initialSortBy = (searchParams.get('sortBy') as SortField) || 'firstName';
  const initialSortOrder = (searchParams.get('sortOrder') as SortOrder) || 'ASC';
  const initialStatus = searchParams.get('status') || '';

  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [sortBy, setSortBy] = useState<SortField>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [statusFilter, setStatusFilter] = useState(initialStatus);

  // Data
  const [items, setItems] = useState<Stakeholder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  // Merge selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [mergeSheetOpen, setMergeSheetOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<Stakeholder | null>(null);
  const [mergeSource, setMergeSource] = useState<Stakeholder | null>(null);
  const [merging, setMerging] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (page > 1) params.set('page', String(page));
    if (limit !== DEFAULT_LIMIT) params.set('limit', String(limit));
    if (sortBy !== 'firstName') params.set('sortBy', sortBy);
    if (sortOrder !== 'ASC') params.set('sortOrder', sortOrder);
    if (statusFilter) params.set('status', statusFilter);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, page, limit, sortBy, sortOrder, statusFilter, setSearchParams]);

  // Load data
  const loadStakeholders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await stakeholderService.getStakeholders({
        search: debouncedSearch || undefined,
        page,
        limit,
        sortBy,
        sortOrder,
        status: statusFilter || undefined,
      });
      setItems(result.data);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load stakeholders');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    loadStakeholders();
  }, [loadStakeholders]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, limit]);

  // Clear selection when page changes
  useEffect(() => {
    setSelectedIds([]);
  }, [page, debouncedSearch, statusFilter]);

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  // Get current assignment
  const getCurrentAssignment = (s: Stakeholder): Assignment | undefined => {
    if (!s.assignments || s.assignments.length === 0) return undefined;
    return s.assignments.find(a => a.isCurrent) || s.assignments[0];
  };

  // Fuzzy highlight on current page
  const highlightedItems = useMemo(() => {
    if (!debouncedSearch) return items.map(item => ({ item, score: 1 }));
    return fuzzyFilter(items, debouncedSearch, (s) => [
      `${s.firstName} ${s.lastName}`,
      s.professionalEmail || '',
      s.personalEmail || '',
      s.category || '',
      s.location || '',
    ]);
  }, [items, debouncedSearch]);

  // Delete handler
  const handleDeleteConfirm = async () => {
    if (!confirmId) return;
    try {
      setDeletingId(confirmId);
      await stakeholderService.deleteStakeholder(confirmId);
      await loadStakeholders();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete stakeholder');
    } finally {
      setDeletingId(null);
      setConfirmId(null);
    }
  };

  const getInitials = (s: Stakeholder) => {
    return `${(s.firstName || '').charAt(0)}${(s.lastName || '').charAt(0)}`.toUpperCase();
  };

  // ── Selection mode handlers ──

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedIds([]);
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      if (prev.length >= 2) {
        // Replace the oldest selection
        return [prev[1], id];
      }
      return [...prev, id];
    });
  };

  const handleRowClick = (s: Stakeholder) => {
    if (selectionMode) {
      toggleSelection(s.id);
    } else {
      navigate(`/stakeholders/${s.id}`);
    }
  };

  // ── Merge handlers ──

  const handleOpenMerge = () => {
    const first = items.find(s => s.id === selectedIds[0]);
    const second = items.find(s => s.id === selectedIds[1]);
    if (!first || !second) return;
    setMergeTarget(first);
    setMergeSource(second);
    setMergeSheetOpen(true);
  };

  const handleSwapMerge = () => {
    setMergeTarget(mergeSource);
    setMergeSource(mergeTarget);
  };

  const handleConfirmMerge = async (resolvedFields: Record<string, any>) => {
    if (!mergeSource || !mergeTarget) return;
    setMerging(true);
    try {
      await stakeholderService.mergeStakeholders(mergeSource.id, mergeTarget.id, resolvedFields);
      showSuccess('Merge Complete', `${mergeSource.firstName} ${mergeSource.lastName} has been merged into ${mergeTarget.firstName} ${mergeTarget.lastName}.`);
      setMergeSheetOpen(false);
      setSelectionMode(false);
      setSelectedIds([]);
      await loadStakeholders();
    } catch (err: any) {
      showError('Merge Failed', err.response?.data?.message || err.message || 'Failed to merge stakeholders.');
    } finally {
      setMerging(false);
    }
  };

  const showMergeButton = canMergeStakeholders(user);
  const filtered = !!debouncedSearch || !!statusFilter;

  const rowActions = (s: Stakeholder): MenuItem[] => {
    const actions: MenuItem[] = [];
    if (canUpdateStakeholder(user, s)) {
      actions.push({ label: 'Edit', icon: Pencil, onSelect: () => navigate(`/stakeholders/${s.id}/edit`) });
    }
    if (canDeleteStakeholder(user, s)) {
      actions.push({
        label: deletingId === s.id ? 'Deleting…' : 'Delete',
        icon: Trash2,
        danger: true,
        disabled: deletingId === s.id,
        onSelect: () => setConfirmId(s.id),
      });
    }
    return actions;
  };

  const meta = selectionMode
    ? 'Select 2 to merge'
    : loading && total === 0
      ? ' '
      : `${total} ${total === 1 ? 'person' : 'people'}`;

  const empty = (
    <EmptyState
      icon={Users}
      title={filtered ? 'No matches' : 'No stakeholders yet'}
      action={
        filtered ? (
          <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setStatusFilter(''); }}>
            Clear filters
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <DashboardLayout>
      <Page>
        <PageHeader
          title="Stakeholders"
          meta={meta}
          actions={
            <>
              {showMergeButton && (
                <Button
                  size="sm"
                  variant={selectionMode ? 'dark' : 'secondary'}
                  onClick={toggleSelectionMode}
                  aria-pressed={selectionMode}
                >
                  {selectionMode ? (
                    'Cancel'
                  ) : (
                    <>
                      <ArrowLeftRight className="h-4 w-4" strokeWidth={1.75} />
                      Merge
                    </>
                  )}
                </Button>
              )}
              <Button size="sm" variant="primary" onClick={() => navigate('/stakeholders/new')}>
                <Plus className="h-4 w-4" strokeWidth={2} />
                Add
              </Button>
            </>
          }
        />

        {/* Search & filters */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SearchInput
            className="w-full sm:w-72"
            placeholder="Search"
            aria-label="Search stakeholders"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <SegmentedControl<StatusValue>
            label="Status"
            items={STATUS_ITEMS}
            value={(statusFilter || 'all') as StatusValue}
            onChange={(value) => setStatusFilter(value === 'all' ? '' : value)}
          />
        </div>

        {/* Error */}
        {error && !loading && (
          <p
            role="alert"
            className="mb-3 flex items-center gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700"
          >
            <CircleAlert className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
            <span className="min-w-0 flex-1">{error}</span>
            <button
              type="button"
              onClick={() => { setError(null); loadStakeholders(); }}
              className="flex-shrink-0 font-medium underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </p>
        )}

        <Card>
          {/* Desktop table */}
          <div className="hidden sm:block">
            <Table>
              <THead>
                <tr>
                  {selectionMode && (
                    <TH scope="col" className="w-10 pr-0">
                      <span className="sr-only">Select</span>
                    </TH>
                  )}
                  <SortTH label="Name" field="firstName" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <TH scope="col">Organisation</TH>
                  <TH scope="col" className="hidden md:table-cell">Role</TH>
                  <SortTH
                    label="Category"
                    field="category"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="hidden xl:table-cell"
                  />
                  <SortTH label="Status" field="status" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <TH scope="col" className="w-12">
                    <span className="sr-only">Actions</span>
                  </TH>
                </tr>
              </THead>
              <TBody>
                {loading ? (
                  Array.from({ length: Math.min(limit, 10) }).map((_, i) => (
                    <tr key={i}>
                      {selectionMode && <TD className="pr-0"><Skeleton className="h-4 w-4 rounded-[5px]" /></TD>}
                      <TD>
                        <div className="flex items-center gap-2.5">
                          <Skeleton className="h-7 w-7 rounded-full" />
                          <Skeleton className="h-3.5 w-28" />
                        </div>
                      </TD>
                      <TD><Skeleton className="h-3.5 w-32" /></TD>
                      <TD className="hidden md:table-cell"><Skeleton className="h-3.5 w-24" /></TD>
                      <TD className="hidden xl:table-cell"><Skeleton className="h-5 w-24 rounded-full" /></TD>
                      <TD><Skeleton className="h-5 w-16 rounded-full" /></TD>
                      <TD />
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7}>{empty}</td>
                  </tr>
                ) : (
                  highlightedItems.map(({ item: s }) => {
                    const a = getCurrentAssignment(s);
                    const orgName = a?.organizationName || (a as any)?.organization?.name;
                    const isSelected = selectedIds.includes(s.id);
                    const actions = rowActions(s);
                    const name = `${s.firstName} ${s.lastName}`;
                    return (
                      <TR
                        key={s.id}
                        interactive
                        aria-selected={selectionMode ? isSelected : undefined}
                        className={isSelected ? 'bg-primary-50/50 hover:bg-primary-50' : ''}
                        onClick={() => handleRowClick(s)}
                      >
                        {selectionMode && (
                          <TD className="pr-0" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleSelection(s.id)}
                              aria-label={`Select ${name}`}
                            />
                          </TD>
                        )}
                        <TD>
                          <div className="flex min-w-0 items-center gap-2.5">
                            <Avatar initials={getInitials(s)} kind="stakeholder" size="sm" />
                            <span
                              className="block max-w-[220px] truncate"
                              title={s.professionalEmail || s.personalEmail || undefined}
                            >
                              <HighlightedText text={name} query={debouncedSearch} />
                            </span>
                            <LockMarker record={s} />
                          </div>
                        </TD>
                        <TD className="text-neutral-500">
                          <span className="block max-w-[220px] truncate" title={orgName || undefined}>
                            {orgName || <span className="text-neutral-300">—</span>}
                          </span>
                        </TD>
                        <TD className="hidden text-neutral-500 md:table-cell">
                          <span className="block max-w-[200px] truncate" title={a?.role || undefined}>
                            {a?.role || <span className="text-neutral-300">—</span>}
                          </span>
                        </TD>
                        <TD className="hidden xl:table-cell">
                          {s.category ? (
                            <CategoryPill
                              label={categoryLabel(s.category)}
                              color={colorOf('stakeholder_category', s.category)}
                              className="max-w-[180px]"
                              title={s.category}
                            />
                          ) : (
                            <span className="text-neutral-300">—</span>
                          )}
                        </TD>
                        <TD>
                          <StatusPill status={s.status} />
                        </TD>
                        <TD className="pl-0 pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                          {!selectionMode && actions.length > 0 && (
                            <Menu
                              items={actions}
                              trigger={(props) => (
                                <IconButton label={`Actions for ${name}`} size="sm" {...props}>
                                  <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
                                </IconButton>
                              )}
                            />
                          )}
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </div>

          {/* Phone rows */}
          <ul className="divide-y divide-neutral-200/70 sm:hidden">
            {loading ? (
              Array.from({ length: Math.min(limit, 8) }).map((_, i) => (
                <li key={i} className="flex h-14 items-center gap-3 px-4">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3 w-44" />
                  </div>
                </li>
              ))
            ) : items.length === 0 ? (
              <li>{empty}</li>
            ) : (
              highlightedItems.map(({ item: s }) => {
                const a = getCurrentAssignment(s);
                const orgName = a?.organizationName || (a as any)?.organization?.name;
                const isSelected = selectedIds.includes(s.id);
                const subtitle = [a?.role, orgName].filter(Boolean).join(' · ');
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => handleRowClick(s)}
                      role={selectionMode ? 'checkbox' : undefined}
                      aria-checked={selectionMode ? isSelected : undefined}
                      className={`flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left transition-colors focus-visible:bg-neutral-50 focus-visible:outline-none active:bg-neutral-100 ${
                        isSelected ? 'bg-primary-50/50' : ''
                      }`}
                    >
                      {selectionMode && (
                        <span
                          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                            isSelected ? 'bg-primary-600 text-white' : 'ring-1 ring-inset ring-neutral-300'
                          }`}
                          aria-hidden="true"
                        >
                          {isSelected && <Check className="h-3 w-3" strokeWidth={2.5} />}
                        </span>
                      )}
                      <Avatar initials={getInitials(s)} kind="stakeholder" size="md" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-[15px] text-ink">
                            <HighlightedText text={`${s.firstName} ${s.lastName}`} query={debouncedSearch} />
                          </span>
                          {s.status !== 'approved' && <StatusPill status={s.status} />}
                          <LockMarker record={s} />
                        </span>
                        <span className="mt-0.5 block truncate text-[13px] text-neutral-500">
                          {subtitle || (s.category ? categoryLabel(s.category) : '—')}
                        </span>
                      </span>
                      {!selectionMode && (
                        <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </Card>

        {/* Pagination */}
        {!loading && items.length > 0 && (
          <Pagination
            currentPage={page}
            totalItems={total}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => { setLimit(newSize); setPage(1); }}
            pageSizeOptions={[10, 25, 50]}
            className="mt-2"
            bottomOffset={selectionMode && selectedIds.length > 0 ? SELECTION_BAR_CLEARANCE : undefined}
          />
        )}

        {/* Room for the selection action bar */}
        {selectionMode && selectedIds.length > 0 && <div className="h-20" />}
      </Page>

      {/* Selection action bar */}
      {selectionMode && selectedIds.length > 0 && (
        <SelectionActionBar
          selectedCount={selectedIds.length}
          maxSelection={2}
          onMerge={handleOpenMerge}
          onCancel={toggleSelectionMode}
          mergeLabel="Merge"
        />
      )}

      {/* Merge resolution sheet */}
      {mergeSheetOpen && mergeSource && mergeTarget && (
        <MergeResolutionSheet
          open={mergeSheetOpen}
          onClose={() => setMergeSheetOpen(false)}
          entityType="stakeholder"
          sourceData={mergeSource as any}
          targetData={mergeTarget as any}
          sourceLabel={`${mergeSource.firstName} ${mergeSource.lastName}`}
          targetLabel={`${mergeTarget.firstName} ${mergeTarget.lastName}`}
          fieldGroups={STAKEHOLDER_MERGE_FIELDS}
          onSwap={handleSwapMerge}
          onConfirmMerge={handleConfirmMerge}
          merging={merging}
          transferSummary={
            (mergeSource.assignments?.length || 0) > 0
              ? `${mergeSource.assignments?.length} assignment${(mergeSource.assignments?.length || 0) !== 1 ? 's' : ''} will be transferred.`
              : undefined
          }
        />
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        open={!!confirmId}
        title="Delete stakeholder"
        message={<span>This can't be undone.</span>}
        confirmText="Delete"
        loading={!!deletingId}
        onCancel={() => setConfirmId(null)}
        onConfirm={handleDeleteConfirm}
      />
    </DashboardLayout>
  );
};
